import { faArrowRotateRight, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useMemo } from 'react';

import { SUPPORTED_CURRENCIES } from '../../../constants/websocket-events';
import { CurrencyTypeEnum } from '../../../model/currency_type.enum';
import { KEYS } from '../../../providers/layout-hook';
import { CustomWidget, WidgetKeys } from '../../../providers/model/custom-widget';
import { getTokenPairName } from '../../../providers/otc-trader/quick-pricer-provider';
import useWidgets from '../../../providers/use-widgets';
import QuickPricer from '../components/quick-pricer';

import './styles.scss';

const DEFAULT_CLEAR_LAYOUT = {
  [KEYS.LG]: [{ i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 8 }],
  [KEYS.MD]: [{ i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 8 }],
  [KEYS.SM]: [{ i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 8 }],
  [KEYS.XS]: [{ i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 8 }],
  [KEYS.XXS]: [{ i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 8 }],
};

export const DEFAULT_LAYOUTS_TOKENS = {
  [KEYS.LG]: [
    { i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 6, h: 6 },
    { i: WidgetKeys.VolSurface, x: 6, y: 0, w: 6, h: 6 },
    { i: WidgetKeys.VolatilitySmile, x: 0, y: 6, w: 6, h: 6 },
    { i: WidgetKeys.MarketHistory, x: 6, y: 6, w: 6, h: 6 },
  ],
  [KEYS.MD]: [
    { i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 6 },
    { i: WidgetKeys.VolSurface, x: 0, y: 6, w: 12, h: 6 },
    { i: WidgetKeys.VolatilitySmile, x: 0, y: 12, w: 12, h: 6 },
    { i: WidgetKeys.MarketHistory, x: 0, y: 18, w: 12, h: 6 },
  ],
  [KEYS.SM]: [
    { i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 6 },
    { i: WidgetKeys.VolSurface, x: 0, y: 6, w: 12, h: 6 },
    { i: WidgetKeys.VolatilitySmile, x: 0, y: 12, w: 12, h: 6 },
    { i: WidgetKeys.MarketHistory, x: 0, y: 18, w: 12, h: 6 },
  ],
  [KEYS.XS]: [
    { i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 6 },
    { i: WidgetKeys.VolSurface, x: 0, y: 6, w: 12, h: 6 },
    { i: WidgetKeys.VolatilitySmile, x: 0, y: 12, w: 12, h: 6 },
    { i: WidgetKeys.MarketHistory, x: 0, y: 18, w: 12, h: 6 },
  ],
  [KEYS.XXS]: [
    { i: WidgetKeys.VolatilitySurfaceMarking, x: 0, y: 0, w: 12, h: 6 },
    { i: WidgetKeys.VolSurface, x: 0, y: 6, w: 12, h: 6 },
    { i: WidgetKeys.VolatilitySmile, x: 0, y: 12, w: 12, h: 6 },
    { i: WidgetKeys.MarketHistory, x: 0, y: 18, w: 12, h: 6 },
  ],
};

const DEFAULT_WIDGETS: CustomWidget[] = [
  {
    key: WidgetKeys.VolatilitySurfaceMarking,
    id: WidgetKeys.VolatilitySurfaceMarking,
    title: 'Volatility Surface Marking',
  },
];
const createDefaultWidgetsWithToken = (): CustomWidget[] => [
  {
    key: WidgetKeys.VolatilitySurfaceMarking,
    title: 'Volatility Surface Marking',
    id: WidgetKeys.VolatilitySurfaceMarking,
  },
  {
    key: WidgetKeys.VolSurface,
    title: 'Volatility Surface',
    id: WidgetKeys.VolSurface,
  },
  {
    key: WidgetKeys.VolatilitySmile,
    title: 'Volatility Smile',
    id: WidgetKeys.VolatilitySmile,
  },
  {
    key: WidgetKeys.MarketHistory,
    title: 'Market History',
    id: WidgetKeys.MarketHistory,
  },
];

const getCurrencyEnum = (currency: string): CurrencyTypeEnum | null => {
  switch (currency) {
    case 'BTC':
      return CurrencyTypeEnum.BITCOIN;
    case 'ETH':
      return CurrencyTypeEnum.ETHEREUM;
    case 'SOL':
      return CurrencyTypeEnum.SOL;
    case 'XRP':
      return CurrencyTypeEnum.XRP;
    default:
      return null;
  }
};

const SurfaceMarking = ({ tab }: { tab: string }) => {
  const defaultWidgets = useMemo(() => {
    if (tab) {
      const tokenTab = getTokenPairName(tab)?.split('_').length
        ? getTokenPairName(tab)?.split('_')[0]
        : null;
      const isSupported =
        tokenTab && !!SUPPORTED_CURRENCIES.find((sc) => !!sc.startsWith(`${tokenTab}`));
      if (isSupported) {
        const currency = getCurrencyEnum(tokenTab);
        if (currency) {
          return {
            widgets: createDefaultWidgetsWithToken(),
            layout: DEFAULT_LAYOUTS_TOKENS,
          };
        }
      }
    }

    return { widgets: DEFAULT_WIDGETS, layout: DEFAULT_CLEAR_LAYOUT };
  }, [tab]);

  const { renderWidgets, handleOpenModal, handleResetLayout, renderModal } = useWidgets(
    `SURFACE_MARKINGS_${tab}`,
    defaultWidgets.widgets,
    defaultWidgets.layout,
    false,
    true,
    DEFAULT_WIDGETS,
  );

  return (
    <div className="surface-marking-container">
      <QuickPricer />
      <div className="main-content">{renderWidgets()}</div>
      <div className="layout-buttons">
        <Button onClick={handleOpenModal} className="btn-add-widget">
          <FontAwesomeIcon icon={faPlus} className="icon" />
          <span>Add widget</span>
        </Button>
        <Button onClick={handleResetLayout} className="btn-restore">
          <FontAwesomeIcon icon={faArrowRotateRight} />
          <span>Restore layout</span>
        </Button>

        {renderModal()}
      </div>
    </div>
  );
};

export default SurfaceMarking;
